import React, { useState } from 'react';
import { Client, PipelineStage, clientsAPI } from '../../services/api';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';

interface ClientStageModalProps {
  client: Client;
  stages: PipelineStage[];
  onClose: () => void;
}

const ClientStageModal: React.FC<ClientStageModalProps> = ({
  client,
  stages,
  onClose,
}) => {
  const [selectedStageId, setSelectedStageId] = useState<number>(client.stageId || 0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStageId || selectedStageId === client.stageId) {
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await clientsAPI.moveStage(client.id, selectedStageId, notes || undefined);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to move client');
    } finally {
      setLoading(false);
    }
  };

  const currentStage = stages.find(s => s.id === client.stageId);
  const targetStage = stages.find(s => s.id === selectedStageId);

  return (
    <Modal isOpen={true} onClose={onClose} title="Move Client" size="md">
      {/* Client Info */}
      <Card variant="bordered" className="mb-6">
        <CardBody className="p-4">
          <h4 className="font-semibold text-neutral-900">{client.name}</h4>
          <p className="text-sm text-neutral-500">{client.type}</p>
        </CardBody>
      </Card>

      {/* Current to Target Stage */}
      {currentStage && targetStage && selectedStageId !== client.stageId && (
        <div className="mb-6 flex items-center justify-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            {currentStage.color && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: currentStage.color }}
              />
            )}
            <span className="font-medium">{currentStage.name}</span>
          </div>
          <ArrowRightIcon className="h-4 w-4 text-neutral-400" />
          <div className="flex items-center space-x-2">
            {targetStage.color && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: targetStage.color }}
              />
            )}
            <span className="font-medium text-primary-600">{targetStage.name}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Select Target Stage
          </label>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {stages.map((stage) => (
              <label
                key={stage.id}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedStageId === stage.id
                    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                    : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                } ${stage.id === client.stageId ? 'opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name="stage"
                  value={stage.id}
                  checked={selectedStageId === stage.id}
                  onChange={() => setSelectedStageId(stage.id)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3 flex-1">
                  {stage.color && (
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900">
                      {stage.name}
                      {stage.id === client.stageId && (
                        <span className="ml-2 text-xs text-neutral-500">(Current)</span>
                      )}
                    </p>
                    {stage.description && (
                      <p className="text-xs text-neutral-500 truncate">{stage.description}</p>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-colors"
            placeholder="Add a note about this stage change..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={selectedStageId === client.stageId}
          >
            Move Client
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ClientStageModal;

